import React from 'react';
import {Trans} from 'react-i18next';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {formatNumberWithCommas} from '@di-shared/utils';
import {drawArcPath} from '@di-shared/utils/drawArcPath';
import useUnitConverter from '@di-shared/utils/useUnitConverter';

const OFFSET = 4;
const SECTION_ANGLE_BOUNDARY = [
  {START: 90 + 60 * 2 + OFFSET, END: 90 + 60 * 3 - OFFSET},
  {
    START: 90 + 60 + OFFSET,
    END: 90 + 60 * 2 - OFFSET,
  },
  {START: 90 + OFFSET, END: 90 + 60 - OFFSET},
  {START: -90 + OFFSET, END: 90 + -OFFSET},
];

export enum MileageGraphColor {
  BASE_COLOR = 'var(--surface-container-low)',
  SECTION1_COLOR = 'var(--quaternary-low)',
  SECTION2_COLOR = 'var(--tertiary)',
  SECTION3_COLOR = 'var(--outline-control-lowest)',
  SECTION4_COLOR = 'var(--graph-gauge-active)',
  NOT_CURRENT_SECTION_SUCCESS_COLOR = 'var(--on-surface-container-high)',
  NOT_CURRENT_SECTION_FAIL_COLOR = 'var(--primary)',
}

interface GrahProps {
  graphSize?: number;
  sections: [
    {ratio: number; color: MileageGraphColor} | null,
    {ratio: number; color: MileageGraphColor} | null,
    {ratio: number; color: MileageGraphColor} | null,
    {ratio: number; color: MileageGraphColor} | null,
  ];
}

interface ArcProps {
  startAngle: number;
  endAngle: number;
  color: MileageGraphColor;
}

function MileageChallengeGraph({graphSize = 220, sections}: GrahProps) {
  const CENTER_X = graphSize / 2;
  const CENTER_Y = graphSize / 2;
  const strokeWidth = Math.round((8 / 220) * graphSize);
  const RADIUS = (graphSize - strokeWidth * 2) / 2;

  function Arc({startAngle, endAngle, color}: ArcProps) {
    return (
      <path
        d={drawArcPath(startAngle, endAngle, RADIUS, CENTER_X, CENTER_Y)}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    );
  }

  return (
    <svg
      width={graphSize}
      height={graphSize}
      viewBox={`0 0 ${graphSize} ${graphSize}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {SECTION_ANGLE_BOUNDARY.map((section) => (
        <Arc
          key={section.START + section.END + MileageGraphColor.BASE_COLOR}
          startAngle={section.START}
          endAngle={section.END}
          color={MileageGraphColor.BASE_COLOR}
        />
      ))}

      {SECTION_ANGLE_BOUNDARY.map((sectionBoundary, index) => {
        const section = sections[index];
        if (!section) {
          return null;
        }
        const ratio = Math.min(1, Math.max(0, section.ratio));
        const partialSectionStart = sectionBoundary.END - (sectionBoundary.END - sectionBoundary.START) * ratio;
        return (
          <Arc
            key={partialSectionStart + sectionBoundary.END + section.color}
            startAngle={partialSectionStart}
            endAngle={sectionBoundary.END}
            color={section.color}
          />
        );
      })}
    </svg>
  );
}

export default MileageChallengeGraph;

export enum MileageChallengeTab {
  '70K',
  '50K',
  '30K',
  '20K',
  '10K',
}

interface MileageChallengeSectionGraph {
  mileageChallengeTab: MileageChallengeTab;
  limitDistance: number;
  tablimitDistance: number;
  previousLimitDistance: number;
  odometer: number;
}

export function MileageChallenge70KGraph({
  odometer,
  mileageChallengeTab,
  limitDistance,
  previousLimitDistance,
  tablimitDistance,
}: MileageChallengeSectionGraph) {
  const {t} = useInferableTranslation();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const isSuccess = tablimitDistance - odometer >= 0;
  const ratio = (odometer - limitDistance) / (previousLimitDistance - limitDistance);

  return (
    <div className="eco_mileage_graph_area">
      <MileageChallengeGraph
        sections={[
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['20K']
                ? MileageGraphColor.SECTION1_COLOR
                : MileageGraphColor.NOT_CURRENT_SECTION_SUCCESS_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['30K']
                ? MileageGraphColor.SECTION2_COLOR
                : MileageGraphColor.NOT_CURRENT_SECTION_SUCCESS_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['50K']
                ? MileageGraphColor.SECTION3_COLOR
                : MileageGraphColor.NOT_CURRENT_SECTION_SUCCESS_COLOR,
          },
          {
            ratio: ratio,
            color:
              mileageChallengeTab === MileageChallengeTab['70K']
                ? MileageGraphColor.SECTION4_COLOR
                : MileageGraphColor.NOT_CURRENT_SECTION_SUCCESS_COLOR,
          },
        ]}
      />
      <div className="eco_mileage_graph_info">
        {isSuccess ? (
          <p
            className="eco_mileage_graph_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            <Trans
              i18nKey="T30231_$value_$unit"
              values={{
                value: formatNumberWithCommas(convertDistanceValue(Math.max(tablimitDistance - odometer, 0))),
                unit: convertDistanceSymbol(Math.max(tablimitDistance - odometer, 0)),
              }}
              components={{
                Emphasis: <em className="text_emphasis" />,
                Value: <span className="value" />,
                Unit: <span className="unit" />,
              }}
            />
          </p>
        ) : (
          <p
            className="eco_mileage_graph_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T30232')}
          </p>
        )}
      </div>
    </div>
  );
}

export function MileageChallenge50KGraph({
  odometer,
  mileageChallengeTab,
  limitDistance,
  previousLimitDistance,
  tablimitDistance,
}: MileageChallengeSectionGraph) {
  const {t} = useInferableTranslation();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const isSuccess = tablimitDistance - odometer >= 0;
  const ratio = (odometer - limitDistance) / (previousLimitDistance - limitDistance);
  return (
    <div className="eco_mileage_graph_area">
      <MileageChallengeGraph
        sections={[
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['20K']
                ? MileageGraphColor.SECTION1_COLOR
                : MileageGraphColor.NOT_CURRENT_SECTION_SUCCESS_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['30K']
                ? MileageGraphColor.SECTION2_COLOR
                : MileageGraphColor.NOT_CURRENT_SECTION_SUCCESS_COLOR,
          },
          {
            ratio: ratio,
            color:
              mileageChallengeTab === MileageChallengeTab['50K']
                ? MileageGraphColor.SECTION3_COLOR
                : MileageGraphColor.NOT_CURRENT_SECTION_SUCCESS_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['70K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
        ]}
      />
      <div className="eco_mileage_graph_info">
        {isSuccess ? (
          <p
            className="eco_mileage_graph_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            <Trans
              i18nKey="T30231_$value_$unit"
              values={{
                value: formatNumberWithCommas(convertDistanceValue(Math.max(tablimitDistance - odometer, 0))),
                unit: convertDistanceSymbol(Math.max(tablimitDistance - odometer, 0)),
              }}
              components={{
                Emphasis: <em className="text_emphasis" />,
                Value: <span className="value" />,
                Unit: <span className="unit" />,
              }}
            />
          </p>
        ) : (
          <p
            className="eco_mileage_graph_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T30232')}
          </p>
        )}
      </div>
    </div>
  );
}

export function MileageChallenge30KGraph({
  odometer,
  mileageChallengeTab,
  limitDistance,
  previousLimitDistance,
  tablimitDistance,
}: MileageChallengeSectionGraph) {
  const {t} = useInferableTranslation();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const isSuccess = tablimitDistance - odometer >= 0;
  const ratio = (odometer - limitDistance) / (previousLimitDistance - limitDistance);
  return (
    <div className="eco_mileage_graph_area">
      <MileageChallengeGraph
        sections={[
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['20K']
                ? MileageGraphColor.SECTION1_COLOR
                : MileageGraphColor.NOT_CURRENT_SECTION_SUCCESS_COLOR,
          },
          {
            ratio: ratio,
            color:
              mileageChallengeTab === MileageChallengeTab['30K']
                ? MileageGraphColor.SECTION2_COLOR
                : MileageGraphColor.NOT_CURRENT_SECTION_SUCCESS_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['50K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['70K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
        ]}
      />
      <div className="eco_mileage_graph_info">
        {isSuccess ? (
          <p
            className="eco_mileage_graph_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            <Trans
              i18nKey="T30231_$value_$unit"
              values={{
                value: formatNumberWithCommas(convertDistanceValue(Math.max(tablimitDistance - odometer, 0))),
                unit: convertDistanceSymbol(Math.max(tablimitDistance - odometer, 0)),
              }}
              components={{
                Emphasis: <em className="text_emphasis" />,
                Value: <span className="value" />,
                Unit: <span className="unit" />,
              }}
            />
          </p>
        ) : (
          <p
            className="eco_mileage_graph_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T30232')}
          </p>
        )}
      </div>
    </div>
  );
}

export function MileageChallenge20KGraph({
  odometer,
  mileageChallengeTab,
  limitDistance,
  previousLimitDistance,
  tablimitDistance,
}: MileageChallengeSectionGraph) {
  const {t} = useInferableTranslation();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const isSuccess = tablimitDistance - odometer >= 0;
  const ratio = (odometer - limitDistance) / (previousLimitDistance - limitDistance);
  return (
    <div className="eco_mileage_graph_area">
      <MileageChallengeGraph
        sections={[
          {
            ratio: ratio,
            color:
              mileageChallengeTab === MileageChallengeTab['20K']
                ? MileageGraphColor.SECTION1_COLOR
                : MileageGraphColor.NOT_CURRENT_SECTION_SUCCESS_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['30K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['50K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['70K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
        ]}
      />
      <div className="eco_mileage_graph_info">
        {isSuccess ? (
          <p
            className="eco_mileage_graph_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            <Trans
              i18nKey="T30231_$value_$unit"
              values={{
                value: formatNumberWithCommas(convertDistanceValue(Math.max(tablimitDistance - odometer, 0))),
                unit: convertDistanceSymbol(Math.max(tablimitDistance - odometer, 0)),
              }}
              components={{
                Emphasis: <em className="text_emphasis" />,
                Value: <span className="value" />,
                Unit: <span className="unit" />,
              }}
            />
          </p>
        ) : (
          <p
            className="eco_mileage_graph_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T30232')}
          </p>
        )}
      </div>
    </div>
  );
}

export function MileageChallenge10KGraph({odometer, mileageChallengeTab, tablimitDistance}: MileageChallengeSectionGraph) {
  const {t} = useInferableTranslation();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const isSuccess = tablimitDistance - odometer >= 0;
  return (
    <div className="eco_mileage_graph_area">
      <MileageChallengeGraph
        sections={[
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['20K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['30K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['50K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['70K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
        ]}
      />
      <div className="eco_mileage_graph_info">
        {isSuccess ? (
          <p
            className="eco_mileage_graph_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            <Trans
              i18nKey="T30231_$value_$unit"
              values={{
                value: formatNumberWithCommas(convertDistanceValue(Math.max(tablimitDistance - odometer, 0))),
                unit: convertDistanceSymbol(Math.max(tablimitDistance - odometer, 0)),
              }}
              components={{
                Emphasis: <em className="text_emphasis" />,
                Value: <span className="value" />,
                Unit: <span className="unit" />,
              }}
            />
          </p>
        ) : (
          <p
            className="eco_mileage_graph_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T30232')}
          </p>
        )}
      </div>
    </div>
  );
}

interface MileageChallengeFailGraphProps {
  mileageChallengeTab: MileageChallengeTab;
}

export function MileageChallengeFailGraph({mileageChallengeTab}: MileageChallengeFailGraphProps) {
  const {t} = useInferableTranslation();
  return (
    <div className="eco_mileage_graph_area">
      <MileageChallengeGraph
        sections={[
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['20K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['30K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['50K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color:
              mileageChallengeTab === MileageChallengeTab['70K']
                ? MileageGraphColor.NOT_CURRENT_SECTION_FAIL_COLOR
                : MileageGraphColor.BASE_COLOR,
          },
        ]}
      />
      <div className="eco_mileage_graph_info">
        <p
          className="eco_mileage_graph_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30232')}
        </p>
      </div>
    </div>
  );
}

export function MileageChallengeUpdateGraph() {
  const {t} = useInferableTranslation();
  return (
    <div className="eco_mileage_graph_area">
      <MileageChallengeGraph
        sections={[
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
        ]}
      />
      <div className="eco_mileage_graph_info">
        <p
          className="eco_mileage_graph_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30233')}
        </p>
      </div>
    </div>
  );
}

export function MileageChallengeErrorGraph() {
  const {t} = useInferableTranslation();
  return (
    <div className="eco_mileage_graph_area">
      <MileageChallengeGraph
        sections={[
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
        ]}
      />
      <div className="eco_mileage_graph_info">
        <p
          className="eco_mileage_graph_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30234')}
        </p>
      </div>
    </div>
  );
}

export function MileageChallengeLoadingGraph() {
  const {t} = useInferableTranslation();
  return (
    <div className="eco_mileage_graph_area skeleton">
      <MileageChallengeGraph
        sections={[
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
          {
            ratio: 1,
            color: MileageGraphColor.BASE_COLOR,
          },
        ]}
      />
      <div className="eco_mileage_graph_info">
        <p
          className="eco_mileage_graph_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30234')}
        </p>
      </div>
    </div>
  );
}
